import { useQuery } from "@tanstack/react-query";
import { getDepartedEmployees, getEmployeesByCompany } from "@/api";
import { AttritionRate, Employee, EmployeeCountData, MonthlyHiresAndDepartures } from "@/types";

interface CompanyEmployeesData {
  employees: Employee[]
  generalAttrition: AttritionRate[]
  salesAttrition: AttritionRate[]
  salesCount: EmployeeCountData[]
  engineeringCount: EmployeeCountData[]
  monthlyHiresAndDepartures: MonthlyHiresAndDepartures[]
}

const defaultData: CompanyEmployeesData = {
  employees: [],
  generalAttrition: [],
  salesAttrition: [],
  salesCount: [],
  engineeringCount: [],
  monthlyHiresAndDepartures: [],
}

export const useCompanyEmployees = (companyId?: number, enabled = true) => {
  const query = useQuery<CompanyEmployeesData>({
    queryKey: ["employees", companyId],
    queryFn: () => getEmployeesByCompany(companyId!),
    staleTime: 1000 * 60 * 5,
    enabled: !!companyId && enabled,
  });

  return {
    ...query,
    data: query.data || defaultData,
  }
};

/**
 * Lazy-load the full employee list only when the Departed Employees tab is active.
 * This avoids blocking the main company page load with a heavy ITWorks fetch.
 */
export const useDepartedEmployees = (companyId?: number, enabled = false) => {
  const query = useQuery<Employee[]>({
    queryKey: ["departedEmployees", companyId],
    queryFn: () => getDepartedEmployees(companyId!),
    staleTime: 1000 * 60 * 5,
    enabled: !!companyId && enabled,
  });

  return {
    ...query,
    data: query.data || [],
  }
};
